import {
  Container,
  Title,
  Text,
  Paper,
  Stack,
  Alert,
  Group,
  Button,
  Grid,
  Card,
} from '@mantine/core';
import { useMedplumNavigate } from '@medplum/react';
import {
  IconShield,
  IconUsers,
  IconDatabase,
  IconSettings,
  IconChartBar,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useAdminStats } from './hooks';

// -----------------------------------------------------------------------------
export const AdminPage = (): JSX.Element => {
  const navigate = useMedplumNavigate();
  const { t } = useTranslation('admin');
  const { stats } = useAdminStats();

  // ---------------------------------------------------------------------------
  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group align="center" gap="md">
          <IconShield size={32} color="var(--mantine-color-blue-6)" />
          <div>
            <Title order={1}>{t('title')}</Title>
            <Text c="dimmed">{t('description')}</Text>
          </div>
        </Group>{' '}
        {/* Welcome Alert */}
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title={t('welcomeAlert.title')}
          color="blue"
        >
          {t('welcomeAlert.message')}
        </Alert>{' '}
        {/* Statistics Cards */}
        <Title order={2} size="h3">
          {t('systemOverview.title')}
        </Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            {' '}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                  {t('systemOverview.stats.totalPatients')}
                </Text>
                <IconUsers size={20} color="var(--mantine-color-blue-6)" />
              </Group>
              <Text size="xl" fw={700}>
                {stats.totalPatients.toLocaleString()}
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            {' '}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                  {t('systemOverview.stats.totalPractitioners')}
                </Text>
                <IconUsers size={20} color="var(--mantine-color-green-6)" />
              </Group>
              <Text size="xl" fw={700}>
                {stats.totalPractitioners.toLocaleString()}
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            {' '}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                  {t('systemOverview.stats.totalOrders')}
                </Text>
                <IconDatabase size={20} color="var(--mantine-color-orange-6)" />
              </Group>
              <Text size="xl" fw={700}>
                {stats.totalOrders.toLocaleString()}
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            {' '}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                  {t('systemOverview.stats.totalTasks')}
                </Text>
                <IconChartBar size={20} color="var(--mantine-color-violet-6)" />
              </Group>
              <Text size="xl" fw={700}>
                {stats.totalTasks.toLocaleString()}
              </Text>
            </Card>
          </Grid.Col>
        </Grid>{' '}
        {/* Management Sections */}
        <Title order={2} size="h3">
          {t('managementTools.title')}
        </Title>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="lg" radius="md" withBorder h="100%">
              {' '}
              <Stack gap="md">
                <Group align="center" gap="md">
                  <IconUsers size={24} color="var(--mantine-color-blue-6)" />
                  <Title order={3} size="h4">
                    {t('managementTools.userManagement.title')}
                  </Title>
                </Group>{' '}
                <Text c="dimmed" size="sm">
                  {t('managementTools.userManagement.description')}
                </Text>
                <Button
                  variant="light"
                  onClick={() => navigate('/admin/user-management')}
                >
                  {t('managementTools.userManagement.openButton')}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="lg" radius="md" withBorder h="100%">
              {' '}
              <Stack gap="md">
                <Group align="center" gap="md">
                  <IconSettings
                    size={24}
                    color="var(--mantine-color-green-6)"
                  />
                  <Title order={3} size="h4">
                    {t('managementTools.systemConfiguration.title')}
                  </Title>
                </Group>
                <Text c="dimmed" size="sm">
                  {t('managementTools.systemConfiguration.description')}
                </Text>
                <Button variant="light" disabled>
                  {t('managementTools.comingSoon')}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="lg" radius="md" withBorder h="100%">
              {' '}
              <Stack gap="md">
                <Group align="center" gap="md">
                  <IconChartBar
                    size={24}
                    color="var(--mantine-color-orange-6)"
                  />
                  <Title order={3} size="h4">
                    {t('managementTools.analyticsReports.title')}
                  </Title>
                </Group>
                <Text c="dimmed" size="sm">
                  {t('managementTools.analyticsReports.description')}
                </Text>
                <Button variant="light" disabled>
                  {t('managementTools.comingSoon')}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="lg" radius="md" withBorder h="100%">
              {' '}
              <Stack gap="md">
                <Group align="center" gap="md">
                  <IconDatabase
                    size={24}
                    color="var(--mantine-color-violet-6)"
                  />
                  <Title order={3} size="h4">
                    {t('managementTools.dataManagement.title')}
                  </Title>
                </Group>
                <Text c="dimmed" size="sm">
                  {t('managementTools.dataManagement.description')}
                </Text>
                <Button variant="light" disabled>
                  {t('managementTools.comingSoon')}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
};
